import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Box,
  EditIcon,
  Heading3,
  InfoIcon,
  Notification,
  NotificationType,
  SimpleButton,
  TextField,
  Tooltip
} from 'edenred-ui';
import { useFormContext } from 'react-hook-form';

import { useTheme } from '@mui/material';
import config from '@epi-config';
import { IBenefitGroupDetailContentType } from '@epi-models/containers/ManageBenefitGroups';
import { slideoutMode } from '@epi-constants/slideoutMode';
import { ManageBenefitLunchFormFields } from '@epi-containers/ManageBenefitGroups/helpers';
import { BenefitType, FundsLoadingTypes } from '@epi-constants/benefitTypes';
import { getFirstDayOfNextMonth } from '@epi-helpers/dateHelper';

import { LimitSection } from '../LimitSection';
import { BenefitGroupNameField } from '../BenefitGroupNameField';
import { DailyLimitField } from '../DailyLimitField';
import { FundsLoadingTypeField } from '../FundsLoadingTypeField';
import { AutomaticRepeatCheckbox } from '../AutomaticRepeatCheckbox';
import { DetailDailyLimitStatement } from '../DetailDailyLimitStatement';
import { SelectMerchantsNotification } from '../SelectMerchantsNotification';
import { NotificationBanner } from '../BenefitGroupDetail/BenefitGroupDetail.styles';
import { useBenefitAmounts } from '@epi-containers/ManageBenefitGroups/hooks';
import { startCase } from 'lodash';
import { useSelector } from 'react-redux';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';

const { enableVirikeLoadingType } = config;

enum NotificationKeys {
  Info = 'notificationInfo',
  PendingSchedule = 'notificationInfoPendingSchedule'
}
export const BenefitGroupDetailContent = ({
  detail,
  benefitType,
  onEdit,
  benefitGroup,
  dailyLimitOptions,
  isShowNotificationBanner,
  isShowScheduledNotificationBanner
}: IBenefitGroupDetailContentType) => {
  const { t } = useTranslation();
  const { primaryFrontColor } = useTheme();
  const { watch } = useFormContext();
  const language = useSelector(getApplicationLanguageId);

  const fundsLoadingType = watch(ManageBenefitLunchFormFields.FundsLoadingType);
  const getNotificationBannerText = (notification: string) =>
    t(`components.manage_benefits.${notification}`, {
      date: getFirstDayOfNextMonth(language)
    });
  const benefitMaxAmounts = useBenefitAmounts();
  return (
    <Box position="relative" pl={8.25} pt={2.5}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Heading3 id="employee-benefits-group-title">
          {t('components.manage_benefit_lunch.detail_benefit_settings_header')}
        </Heading3>
        {detail.mode === slideoutMode.view && benefitGroup && (
          <SimpleButton
            id="edit-benefit-group"
            text={t('components.manage_benefit_lunch.detail_edit_btn_text')}
            icon={<EditIcon />}
            onClick={() => onEdit(benefitGroup.groupId)}
          />
        )}
      </Box>
      <Box width={457} my={3.5}>
        <Box pb={4}>
          <TextField
            id="employee-benefits-group-benefit-type"
            label={t(
              'components.manage_benefit_lunch.detail_benefit_type_label'
            )}
            value={t(`components.manage_benefits.new.benefits.${benefitType}`)}
            readOnly
          />
        </Box>
        <Box pb={4}>
          <BenefitGroupNameField
            label={t('components.manage_benefit_lunch.detail_group_name_label')}
            readOnly={detail.mode === slideoutMode.view}
          />
        </Box>
        {benefitType === BenefitType.Lunch ? (
          <LimitSection
            detail={detail}
            benefitType={benefitType}
            currentMaxAmount={benefitGroup?.maxAmountPerPeriod}
          />
        ) : (
          <Box pb={2} display="flex">
            <DailyLimitField
              label={t(
                `components.manage_benefit_${benefitType}.detail_limit_label`
              )}
              readOnly={detail.mode === slideoutMode.view}
              benefitType={benefitType}
              options={dailyLimitOptions}
              name={ManageBenefitLunchFormFields.DailyLimit}
              fundsLoadingTypeName={
                ManageBenefitLunchFormFields.FundsLoadingType
              }
              maxAmounts={benefitMaxAmounts[startCase(benefitType)]}
              currentMaxAmount={benefitGroup?.maxAmountPerPeriod}
            />
            <Tooltip
              arrow
              placement="top"
              id="daily-limit-info-tooltip"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: '500px'
                  }
                }
              }}
              title={
                <Trans
                  i18nKey={`components.manage_benefit_${benefitType}.detail_daily_limit_statement`}
                />
              }
            >
              <Box pl={1} pt={3} id="daily-limit-info-icon">
                <InfoIcon color={primaryFrontColor} />
              </Box>
            </Tooltip>
          </Box>
        )}
        {benefitType === BenefitType.Lunch && (
          <FundsLoadingTypeField
            name={ManageBenefitLunchFormFields.FundsLoadingType}
            disabled={detail.mode === slideoutMode.view}
            benefitType={benefitType}
          />
        )}
        {enableVirikeLoadingType &&
          benefitType === BenefitType.Recreational && (
            <FundsLoadingTypeField
              name={ManageBenefitLunchFormFields.FundsLoadingType}
              disabled={detail.mode === slideoutMode.view}
              benefitType={benefitType}
            />
          )}
        {benefitType === BenefitType.Lunch &&
          fundsLoadingType === FundsLoadingTypes.TopUpRequest && (
            <AutomaticRepeatCheckbox
              disabled={detail.mode === slideoutMode.view}
              name={ManageBenefitLunchFormFields.AutomaticRepeat}
            />
          )}
        {isShowNotificationBanner && (
          <NotificationBanner>
            <Notification
              type={NotificationType.Important}
              text={getNotificationBannerText(
                isShowScheduledNotificationBanner
                  ? NotificationKeys.PendingSchedule
                  : NotificationKeys.Info
              )}
            />
          </NotificationBanner>
        )}
      </Box>
      <Box pb={7}>
        {(benefitType == BenefitType.Massage ||
          benefitType == BenefitType.Wellbeing) && (
          <SelectMerchantsNotification benefitType={benefitType} />
        )}
        <DetailDailyLimitStatement benefitType={benefitType} />
      </Box>
    </Box>
  );
};
